/**
 * Google Identity Services — the browser half of sign-in.
 *
 * The app asks Google for an id_token and nothing else. There is no access
 * token here, no refresh token, and no `cloud-platform` scope anywhere near
 * the browser: the id_token only says *who* this is, and the broker decides
 * what that person may do. That is the whole point of the hosted architecture,
 * and it is why this file is as small as it is.
 */

/** Minimal typings for the parts of the GIS client this app uses. */
interface CredentialResponse {
  credential?: string
}

interface GoogleAccountsId {
  initialize(config: {
    client_id: string
    callback: (response: CredentialResponse) => void
    hd?: string
    auto_select?: boolean
    cancel_on_tap_outside?: boolean
    use_fedcm_for_prompt?: boolean
    ux_mode?: 'popup' | 'redirect'
  }): void
  renderButton(
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon'
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'small' | 'medium' | 'large'
      text?: 'signin_with' | 'signup_with' | 'continue_with'
      shape?: 'rectangular' | 'pill'
      logo_alignment?: 'left' | 'center'
      width?: number
    }
  ): void
  disableAutoSelect(): void
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } }
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client'

let loader: Promise<GoogleAccountsId> | null = null

/** Loads the GIS client once, resolving with its `accounts.id` namespace. */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (loader) return loader
  loader = new Promise<GoogleAccountsId>((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id)
      return
    }
    const script = document.createElement('script')
    script.src = GIS_SRC
    script.async = true
    script.defer = true
    script.onload = () => {
      const id = window.google?.accounts?.id
      if (id) resolve(id)
      else reject(new Error('Google sign-in loaded but did not start.'))
    }
    script.onerror = () =>
      reject(new Error('Could not load Google sign-in. Check your connection and try again.'))
    document.head.appendChild(script)
  }).catch((e: unknown) => {
    // Let a later attempt retry rather than caching the failure forever.
    loader = null
    throw e
  })
  return loader
}

export interface SignInButtonOptions {
  clientId: string
  /** Pre-filters Google's account chooser to a Workspace domain. */
  hostedDomain?: string
  onCredential: (credential: string) => void
  onError: (message: string) => void
}

/**
 * Renders Google's own sign-in button into `parent`. Google requires that the
 * button be its own rendered element — a custom button posting to the same
 * callback is not a supported (or as safe) integration.
 */
export async function renderSignInButton(
  parent: HTMLElement,
  options: SignInButtonOptions
): Promise<void> {
  const id = await loadGoogleIdentity()
  id.initialize({
    client_id: options.clientId,
    ...(options.hostedDomain ? { hd: options.hostedDomain } : {}),
    callback: (response) => {
      if (response.credential) options.onCredential(response.credential)
      else options.onError('Google did not return a sign-in. Please try again.')
    },
    // No One Tap auto-select: signing a user in without them asking is the
    // wrong default for an app that acts on company data.
    auto_select: false,
    cancel_on_tap_outside: true,
    use_fedcm_for_prompt: true
  })
  id.renderButton(parent, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: 280
  })
}

/** Clears GIS's own "keep me signed in" hint, so Sign out really signs out. */
export function forgetGoogleSession(): void {
  try {
    window.google?.accounts.id.disableAutoSelect()
  } catch {
    /* GIS may never have loaded; nothing to forget */
  }
}
